<template>
  <div class="org-login-container">
    <!-- Maintenance Mode Modal -->
    <div v-if="showMaintenanceModal" class="maintenance-modal-backdrop" @click="showMaintenanceModal = false">
      <div class="maintenance-modal" @click.stop>
        <div class="maintenance-icon">🔧</div>
        <h2>System Under Maintenance</h2>
        <p class="maintenance-message">{{ maintenanceMessage }}</p>
        <button class="maintenance-close-btn" @click="showMaintenanceModal = false">
          Close
        </button>
      </div>
    </div>

    <div class="org-login-card">
      <h1>Development Data Analytics</h1>
      
      <!-- Organization Selection Screen -->
      <div v-if="currentStep === 'org-select'" class="org-select-screen">
        <h3>Select Organization</h3>
        <div class="subtitle">Please select your organization or create a new one.</div>
        
        <div v-if="organizations.length > 0" class="org-list">
          <div 
            v-for="org in organizations" 
            :key="org.id" 
            class="org-item"
            @click="selectOrganization(org)"
          >
            <div class="org-name">{{ org.name }}</div>
            <div class="org-details">{{ org.description }}</div>
          </div>
        </div>
        
        <div v-else class="no-orgs">
          <p>No organizations found.</p>
        </div>
        
        <div class="action-buttons">
          <button class="primary-action-button" @click="currentStep = 'create-org'">
            Create New Organization
          </button>
        </div>
      </div>
      
      <!-- Organization Creation Screen -->
      <div v-if="currentStep === 'create-org'" class="create-org-screen">
        <h3>Create New Organization</h3>
        <div class="subtitle">Enter details for your new organization.</div>
        
        <form @submit.prevent="createOrganization" class="org-form">
          <div class="form-group">
            <label for="orgName">Organization Name</label>
            <input 
              id="orgName"
              type="text" 
              v-model="newOrg.name" 
              placeholder="Organization Name"
              required
            />
          </div>
          
          <div class="form-group">
            <label for="orgDescription">Description (Optional)</label>
            <textarea 
              id="orgDescription"
              v-model="newOrg.description" 
              placeholder="Brief description of your organization"
              rows="3"
            ></textarea>
          </div>
          
          <div class="form-group">
            <label for="adminEmail">Admin Email</label>
            <input 
              id="adminEmail"
              type="email" 
              v-model="newOrg.adminEmail" 
              placeholder="Your email address"
              required
            />
          </div>
          
          <div class="form-group">
            <label for="adminPassword">Admin Password</label>
            <input 
              id="adminPassword"
              type="password" 
              v-model="newOrg.adminPassword" 
              placeholder="Create a password"
              required
            />
          </div>
          
          <div class="form-group">
            <label for="confirmPassword">Confirm Password</label>
            <input 
              id="confirmPassword"
              type="password" 
              v-model="newOrg.confirmPassword" 
              placeholder="Confirm your password"
              required
            />
          </div>
          
          <div class="action-buttons">
            <button type="button" class="secondary-action-button" @click="currentStep = 'org-select'">
              Back
            </button>
            <button type="submit" class="primary-action-button">
              Create Organization
            </button>
          </div>
        </form>
      </div>
      
      <!-- User Login Screen -->
      <div v-if="currentStep === 'user-login'" class="user-login-screen">
        <h3>Sign In to {{ selectedOrg.name }}</h3>
        <div class="subtitle">Enter your credentials to access your organization.</div>
        
        <form @submit.prevent="loginUser" class="login-form">
          <div class="form-group">
            <input
              class="login-form-email"
              type="email"
              v-model="loginEmail"
              placeholder="Email"
              required
            />
          </div>
          
          <div class="form-group">
            <input
              class="login-form-password"
              type="password"
              v-model="loginPassword"
              placeholder="Password"
              required
            />
          </div>
          
          <div v-if="showMFA" class="form-group">
            <input
              class="login-form-mfa-otp"
              type="text"
              v-model="otp"
              placeholder="OTP"
            />
          </div>
          
          <div class="action-buttons">
            <button type="button" class="secondary-action-button" @click="currentStep = 'org-select'">
              Change Organization
            </button>
            <button type="submit" class="primary-action-button">
              Login
            </button>
          </div>
        </form>
        
        <div v-if="showMFA" class="mfa-setup">
          <button class="secondary-action-button" @click="setupMFA = true">
            Setup Multifactor Authentication
          </button>
        </div>
      </div>
      
      <!-- MFA Setup Screen -->
      <div v-if="currentStep === 'user-login' && setupMFA && mfaString.length" class="mfa-setup-screen">
        <h4>Scan this QR Code in your Google Authenticator App</h4>
        <vue-qrcode scale="8" :value="mfaString" />
        <button class="secondary-action-button" @click="setupMFA = false">
          Back to Login
        </button>
      </div>
      
      <error-message class="error-message" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits } from 'vue';
import { useAuthStore } from "@/stores/auth_store";
import { useErrorStore } from "@/stores/error_store";
import { useOrganizationStore } from "@/stores/organization_store";
import ErrorMessage from "@/components/helpers/ErrorMessage.vue";
import VueQrcode from "vue-qrcode";

const emit = defineEmits(['logged-in-attempt']);
const authStore = useAuthStore();
const errorStore = useErrorStore();
const organizationStore = useOrganizationStore();

// Login steps
const currentStep = ref('org-select');
const setupMFA = ref(false);

// Organization data
const organizations = ref([]);
const selectedOrg = ref(null);
const newOrg = ref({
  name: '',
  description: '',
  adminEmail: '',
  adminPassword: '',
  confirmPassword: ''
});

// Login credentials
const loginEmail = ref('');
const loginPassword = ref('');
const otp = ref('');
const mfaString = ref('');

// Maintenance mode
const showMaintenanceModal = ref(false);
const maintenanceMessage = ref('The system is currently under maintenance. Please try again later.');

// Environment checks
const inCypressEnvt = window.Cypress ? true : false;
const forceMFA = !!process.env.VUE_APP_ENV_FORCE_MFA && process.env.VUE_APP_ENV_FORCE_MFA !== 'false' && !inCypressEnvt;
const inDevEnvt = process.env.NODE_ENV === 'development';
const showMFA = computed(() => {
  return !inDevEnvt || forceMFA;
});

// Fetch organizations on component mount
const fetchOrganizations = async () => {
  try {
    const orgs = await organizationStore.fetchOrganizations();
    organizations.value = orgs;
  } catch (error) {
    errorStore.setError('Error fetching organizations', error.message, 'error');
  }
};

// Select an organization and proceed to login
const selectOrganization = (org) => {
  selectedOrg.value = org;
  organizationStore.setCurrentOrganization(org);
  currentStep.value = 'user-login';
};

// Create a new organization
const createOrganization = async () => {
  // Validate passwords match
  if (newOrg.value.adminPassword !== newOrg.value.confirmPassword) {
    errorStore.setError('Password Error', 'Passwords do not match', 'error');
    return;
  }
  
  try {
    const createdOrg = await organizationStore.createOrganization({
      name: newOrg.value.name,
      description: newOrg.value.description,
      admin_email: newOrg.value.adminEmail,
      admin_password: newOrg.value.adminPassword
    });
    
    // Auto-select the newly created organization
    selectedOrg.value = createdOrg;
    organizationStore.setCurrentOrganization(createdOrg);
    
    // Pre-fill login form with admin email
    loginEmail.value = newOrg.value.adminEmail;
    
    // Move to login screen
    currentStep.value = 'user-login';
    
    // Reset the form
    newOrg.value = {
      name: '',
      description: '',
      adminEmail: '',
      adminPassword: '',
      confirmPassword: ''
    };
  } catch (error) {
    errorStore.setError('Organization Creation Error', error.message, 'error');
  }
};

// Login to the selected organization
const loginUser = async () => {
  const data = {
    user: {
      email: loginEmail.value,
      password: loginPassword.value,
      otp: otp.value,
      organization_id: selectedOrg.value.id
    }
  };
  
  try {
    await authStore.loginUser(data);
    
    if (authStore.login_status === 503 || authStore.maintenance_mode) {
      // Maintenance mode
      maintenanceMessage.value = authStore.login_message || 'The system is currently under maintenance. Please try again later.';
      showMaintenanceModal.value = true;
    } else if (authStore.login_status === 401) {
      errorStore.setError('Error Logging In', authStore.login_message, 'error', true);
    } else {
      // If login was successful, set the current organization
      organizationStore.setCurrentOrganization(selectedOrg.value);
      
      // Emit event to parent component
      emit('logged-in-attempt');
    }
  } catch (error) {
    // Check if error response is maintenance mode (503)
    if (error.response?.status === 503 || error.response?.data?.maintenance_mode) {
      maintenanceMessage.value = error.response?.data?.message || 'The system is currently under maintenance. Please try again later.';
      showMaintenanceModal.value = true;
    } else {
      errorStore.setError('Login Error', error.message, 'error');
    }
  }
  
  // Reset form
  loginPassword.value = '';
  otp.value = '';
};

// Watch for MFA setup status
authStore.$subscribe(() => {
  if (authStore.allowMFASetup) {
    mfaString.value = 'otpauth://totp/Dda:' + authStore.getUserEmail + '?secret=' + authStore.getGoogleSecret + '&issuer=dda';
  }
});

// Initialize component
fetchOrganizations();
</script>

<style scoped lang="scss">
@import "./src/assets/main.scss";

.org-login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
}

.org-login-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 30px;
  width: 100%;
  max-width: 500px;
  
  h1 {
    text-align: center;
    margin-bottom: 30px;
    color: $darkGray1;
  }
  
  h3 {
    margin-bottom: 10px;
    color: $darkGray1;
  }
  
  .subtitle {
    color: $mediumGray1;
    margin-bottom: 20px;
    font-size: 14px;
  }
}

.org-list {
  margin-bottom: 20px;
  max-height: 300px;
  overflow-y: auto;
  
  .org-item {
    padding: 15px;
    border: 1px solid $lightGray1;
    border-radius: 4px;
    margin-bottom: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      border-color: $brightBlue1;
      background-color: rgba($brightBlue1, 0.05);
    }
    
    .org-name {
      font-weight: bold;
      font-size: 16px;
      margin-bottom: 5px;
    }
    
    .org-details {
      font-size: 14px;
      color: $mediumGray1;
    }
  }
}

.no-orgs {
  text-align: center;
  padding: 20px;
  color: $mediumGray1;
}

.form-group {
  margin-bottom: 15px;
  
  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
  }
  
  input, textarea {
    width: 100%;
    padding: 10px;
    border: 1px solid $lightGray1;
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
      border-color: $brightBlue1;
      outline: none;
    }
  }
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  
  button {
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    
    &.primary-action-button {
      background-color: $brightBlue1;
      color: white;
      border: none;
      
      &:hover {
        background-color: darken($brightBlue1, 10%);
      }
    }
    
    &.secondary-action-button {
      background-color: transparent;
      color: $brightBlue1;
      border: 1px solid $brightBlue1;
      
      &:hover {
        background-color: rgba($brightBlue1, 0.05);
      }
    }
  }
}

.mfa-setup {
  margin-top: 20px;
  text-align: center;
}

.error-message {
  margin-top: 20px;
}

@media (max-width: 600px) {
  .org-login-card {
    padding: 20px;
  }
  
  .action-buttons {
    flex-direction: column;
    
    button {
      margin-bottom: 10px;
    }
  }
}

// Maintenance Modal Styles
.maintenance-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.maintenance-modal {
  background: white;
  border-radius: 12px;
  padding: 40px;
  max-width: 500px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  
  .maintenance-icon {
    font-size: 60px;
    margin-bottom: 20px;
  }
  
  h2 {
    font-size: 24px;
    color: #333;
    margin-bottom: 15px;
  }
  
  .maintenance-message {
    font-size: 16px;
    color: #666;
    line-height: 1.5;
    margin-bottom: 30px;
  }
  
  .maintenance-close-btn {
    background-color: $brightBlue1;
    color: white;
    border: none;
    padding: 12px 30px;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    
    &:hover {
      background-color: darken($brightBlue1, 10%);
    }
  }
}
</style>